import React from "react";
import { Container, Col, Row, Table } from "reactstrap";
import { Fields } from "@repoflow/components/fields";
import { useSectionContext } from "@repoflow/components/section";
import ContentTabs, { useContentState } from "@repoflow/components/content-tabs";
import { Preview } from "components/steps-preview";
import { Link } from "react-router-dom";
import * as widgets from "@shared/widgets/register";
import { ShareTab } from "./share";
import { ResponsesTab } from "./responses";
import { VersionsTab } from "./versions";
import { PublishBar } from "./publish";

// Hoisted for the reason recorded in ../edit: a config literal is a new object
// every render and the provider rebuilds underneath it.
//
// `steps` is deliberately not among these. It is JSON on the wire, and a
// read-only dump of the raw array would be the only place in the app that shows
// somebody a serialised blob. What this screen says about the steps is the two
// tabs below: what they DECLARE, as a table, and what they COMPILE TO, as the
// forms a person filling the workflow in will meet.
const VIEW_FIELDS = {
  "/Public/Workflow": {
    fields: ["workflowId", "title", "description", "tags"],
  },
};

export const Sidebar = () => null;

export const Indice = () => null;

// What the steps and subforms declare: which ones there are, in order, and how
// many fields each asks for.
//
// The subforms table appears only when there are any. Most workflows will never
// declare one, and an empty second table under a full first says nothing — but
// the heading is unconditional once there IS one, because a subform with no
// fields is worth seeing rather than hiding.
const StepsTab = ({ steps, subforms }) => (
  <>
    <Row className="mt-3">
      <Col>
        <h6 className="text-muted mb-3">Steps</h6>
        {/* Named so a spec can say "the steps table" without matching the
            fields above it, which render no <table>. */}
        <Table id="workflow-steps" size="sm" hover>
          <thead>
            <tr>
              <th>#</th>
              <th>Name</th>
              <th>Label</th>
              <th>Fields</th>
            </tr>
          </thead>
          <tbody>
            {steps.length === 0 ? (
              <tr>
                <td colSpan={4} className="text-muted">
                  No steps yet. Edit the workflow to add one.
                </td>
              </tr>
            ) : (
              steps.map((step, index) => (
                <tr key={step.name || index}>
                  <td>{index + 1}</td>
                  <td>{step.name}</td>
                  <td>{step.label}</td>
                  <td>{(step.params || []).length}</td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </Col>
    </Row>
    {subforms.length > 0 ? (
      <Row className="mt-3">
        <Col>
          <h6 className="text-muted mb-3">Subforms</h6>
          <Table id="workflow-subforms" size="sm" hover>
            <thead>
              <tr>
                <th>Name</th>
                <th>Description</th>
                <th>Fields</th>
              </tr>
            </thead>
            <tbody>
              {subforms.map((subform, index) => (
                <tr key={subform.name || index}>
                  <td>{subform.name}</td>
                  <td>{subform.description}</td>
                  <td>{(subform.params || []).length}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Col>
      </Row>
    ) : null}
  </>
);

// The workflow, its steps, and the things you can do to it.
//
// FIVE TABS. The first two mirror the editor's. What a workflow declares and what it
// compiles to are different questions, and the tables answer only the first: a
// step row saying "4 fields" does not say whether those four are the four you
// meant, or whether an `object` field among them still names a subform that
// exists. Preview answers that, and it is the same component the editor's
// Preview tab renders — over the STORED steps here rather than the unsaved
// ones, which is the whole difference between the two screens.
//
// Both of those are the DRAFT. Published is the other side: every version the
// workflow has been published as, the live one first, each previewed through
// the same component — so what the link serves and what is about to replace
// it sit one tab apart. The bar above the tabs says which is which and is
// where Publish lives (./publish).
//
// Share and Responses are the capture path's two ends: the link a visitor
// fills the workflow in through, and what they sent. Neither belongs on the
// editor, which is about what the workflow ASKS.
//
// Steps is first and selected by default, so the screen still opens on the
// table it always opened on.
export const Content = () => {
  const { values, schema, base } = useSectionContext();
  const steps = (values && values.steps) || [];
  const subforms = (values && values.subforms) || [];
  // Held so that anything re-rendering this screen does not send you back to
  // the first tab, the same reason the editor holds its own.
  const tabs = useContentState({ selected: 0 });

  const items = [
    { label: "Steps", content: <StepsTab steps={steps} subforms={subforms} /> },
    {
      label: "Preview",
      content: (
        <Preview
          steps={steps}
          subforms={subforms}
          widgets={widgets}
          empty="No steps yet. Edit the workflow to add one, and it will be previewed here."
        />
      ),
    },
    { label: "Published", content: <VersionsTab workflow={values || {}} /> },
    { label: "Share", content: <ShareTab workflow={values || {}} /> },
    { label: "Responses", content: <ResponsesTab workflow={values || {}} /> },
  ];

  return (
    <Container className="mt-4">
      <Row>
        <Col>
          <div className="float-right">
            <Link className="btn btn-primary" to={`${base}/edit`}>
              Edit
            </Link>{" "}
            <Link className="btn btn-danger" to={`${base}/remove`}>
              Remove
            </Link>
          </div>
        </Col>
      </Row>
      <Row>
        <Col>
          <Fields
            schema={schema}
            values={values}
            widgets={widgets}
            target={{ $ref: "/Public/Workflow" }}
            config={VIEW_FIELDS}
          />
        </Col>
      </Row>
      <Row>
        <Col>
          <PublishBar workflow={values || {}} />
        </Col>
      </Row>
      <Row className="mt-3">
        <Col>
          <ContentTabs control={tabs} tabs={items} />
        </Col>
      </Row>
    </Container>
  );
};
